import React from "react";
import { Trash2, Pencil } from "lucide-react";
import { Campaign, CampaignItem } from "./index";
import { CandidatCard } from "./candidat";
import { EnseigneCard } from "./enseigne";
import { VilleCard } from "./ville";
import { LocationCard } from "./location";
import { Button } from "../button";
import { Badge } from "../badge";

interface CampaignCardProps {
  campaign: Campaign;
  onDelete: (id: number) => void;
  onEdit: (campaign: Campaign) => void;
  isDeleting: boolean;
}

export function CampaignCard({
  campaign,
  onDelete,
  onEdit,
  isDeleting,
}: CampaignCardProps) {
  return (
    <div className="card rounded-lg shadow-md ring-1 ring-black/5 bg-white h-full flex flex-col relative p-6 overflow-hidden hover:bg-default-50/40 hover:shadow-lg">
      {/* Campaign Header */}
      <div className="grid grid-cols-4 gap-6">
        <div className="flex flex-col">
          <div className="flex-1 min-w-0">
            <h4 className="font-bold text-slate-900 text-lg mb-1 truncate">
              {campaign.title}
            </h4>

            {/* Tags */}
            {campaign.tags && campaign.tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2 mb-2">
                {campaign.tags.map((tag, index) => (
                  <Badge key={index} label={tag} color="indigo" />
                ))}
              </div>
            )}

            <p className="text-xs text-slate-600 mb-3">
              Créé le{" "}
              {new Date(campaign.created_at).toLocaleDateString("fr-FR", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 mt-auto">
            <Button
              onClick={() => onEdit(campaign)}
              disabled={isDeleting}
              variant="light"
              size="xsmall"
              full
              icon={Pencil}
              title="Modifier le projet"
            >
              Modifier
            </Button>
            <Button
              onClick={() => onDelete(campaign.id)}
              disabled={isDeleting}
              variant="light"
              size="xsmall"
              full
              icon={Trash2}
              title="Supprimer le projet"
            >
              Supprimer
            </Button>
          </div>
        </div>

        {/* Campaign Items */}
        {campaign.campaign_items.map((item) => (
          <ProfileCard key={`${item.item_type}-${item.id}`} item={item} />
        ))}
      </div>
    </div>
  );
}

interface ProfileCardProps {
  item: CampaignItem;
}

function ProfileCard({ item }: ProfileCardProps) {
  switch (item.item_type) {
    case "Profile::Candidat":
      return <CandidatCard item={item} />;
    case "Profile::Enseigne":
      return <EnseigneCard item={item} />;
    case "Profile::Ville":
      return <VilleCard item={item} />;
    case "Location":
      return <LocationCard item={item} />;
    default:
      return null;
  }
}
